import { useState } from "react";
import { useTransactionsFilters } from "../../hooks/useTransactionsFilters";
import { RestoredFiltersBanner } from "../../components/RestoredFiltersBanner";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../../components/ui/dialog";
import { BottomSheet } from "../../components/ui/bottom-sheet";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { formatMoney } from "../../lib/api";
import { MoneyAmount } from "../../components/MoneyAmount";
import { LineItemsView } from "../../components/LineItemsView";
import { TagChips, TagsInput } from "../../components/Tags";
import { useTopTags } from "../../hooks/queries";
import { useReceivablesInfinite, useReceivablesMutations, useProfile, useFxRates, useCashAccounts } from "../../hooks/queries";
import { receivablesService } from "../../services";
import { useQueryClient } from "@tanstack/react-query";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, Pencil, RotateCw, AlertCircle, CalendarClock, Calendar as CalendarIcon, Coins, UserMinus, Loader2, Search, X, Wallet } from "lucide-react";
import { nextRecurringDate, formatNextDate } from "../../lib/recurring";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { AccountChip } from "../../components/AccountChip";
import { GroupedListView } from "../../components/GroupedListView";
import { pushHighlight } from "../../lib/highlights";
import { HighlightCard } from "../../components/HighlightCard";
import { AccountPickerDialog } from "../../components/AccountPickerDialog";
import { PageHeader } from "../../components/PageHeader";
import { SwipeActions } from "../../components/SwipeActions";
import { reportValidationError } from "../../lib/validationTelemetry";

// iter-211 — localize the cadence token used in the subscription chip.
// Backend stores English keys ("monthly", "weekly", "biweekly",
// "quarterly", "yearly"). All other chip copy is in the user language.
const CADENCE_LABELS = {
  es: { weekly: "semanal", biweekly: "quincenal", monthly: "mensual", quarterly: "trimestral", yearly: "anual" },
  en: { weekly: "weekly", biweekly: "biweekly", monthly: "monthly", quarterly: "quarterly", yearly: "yearly" },
  pt: { weekly: "semanal", biweekly: "quinzenal", monthly: "mensal", quarterly: "trimestral", yearly: "anual" },
};
function localizeCadence(token, lang) {
  return CADENCE_LABELS[lang]?.[token] || CADENCE_LABELS.en[token] || token;
}

export default function Receivables() {
  const { t, lang } = useLang();
  const isEs = lang === "es";
  const navigate = useNavigate();
  const {
    search, setSearch,
    dateFrom, setDateFrom,
    dateTo, setDateTo,
    tag: tagFilter, setTag: setTagFilter,
    account: accountFilter, setAccount: setAccountFilter,
    groupBy, setGroupBy,
    restored: filtersRestored,
    clear: clearFilters,
    hasActive: filtersActive,
  } = useTransactionsFilters({ persistKey: "receivables.filters" });
  // Cobros realizados (status=collected) ya no se listan aquí: viven en
  // Movimientos. Forzamos status="pending" para que esta pantalla muestre
  // solo lo accionable (lo aún por cobrar). Las recurrentes mantienen
  // status="pending" después de cobrar (avanza expected_date) así que
  // siguen apareciendo de forma natural.
  const debouncedSearch = useDebouncedValue(search, 350);
  const { items, sentinelRef, hasMore, isFetchingNextPage, isLoading } = useReceivablesInfinite({
    limit: 30,
    search: debouncedSearch || undefined,
    status: "pending",
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    tag: tagFilter || undefined,
  });
  // iter-195 — quick filter applied client-side over the already-paged
  // items (overdue / next-7-days). The server query stays the same so
  // we don't fragment cache; the chip just narrows the view.
  const [quickFilter, setQuickFilter] = useState(null);
  const todayMs = new Date().setHours(0, 0, 0, 0);
  const next7Ms = todayMs + 7 * 86400000;
  const filteredItems = items.filter((r) => {
    if (accountFilter && r.account_id !== accountFilter) return false;
    if (!quickFilter) return true;
    const dueMs = new Date(r.expected_date || r.date).setHours(0, 0, 0, 0);
    if (quickFilter === "overdue") return dueMs < todayMs;
    if (quickFilter === "next7") return dueMs >= todayMs && dueMs <= next7Ms;
    return true;
  });
  const { data: profile } = useProfile();
  const currency = profile?.currency || "USD";
  const { create, update, remove: removeMut, collect: collectMut } = useReceivablesMutations();
  const queryClient = useQueryClient();
  const { data: tagsData } = useTopTags();
  const tagSuggestions = (tagsData?.by_kind?.receivables || []).map((r) => r.tag);
  const { data: cashAccounts = [] } = useCashAccounts();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // iter-169zze · field-level error map so the user sees WHICH input
  // is missing instead of a generic "Fill all required fields" toast.
  const [fieldErrors, setFieldErrors] = useState({});
  const [form, setForm] = useState({
    client_name: "", amount: "", expected_date: "", probability: "high",
    recurring: false, kind: null, frequency: "monthly", end_date: "",
    original_currency: "", notes: "", tags: [], account_id: "",
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  // iter-211 — receivable whose cancel-subscription dialog is open.
  // Carries `{ row, reason }`. Reason is optional free text.
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  // Picker target: receivable whose collect action needs an explicit account.
  const [pickerTarget, setPickerTarget] = useState(null);
  const { data: fxInfo } = useFxRates();
  const fxList = Object.keys(fxInfo?.rates || {}).sort();

  const openNew = () => {
    setEditing(null);
    setFieldErrors({});
    setForm({ client_name: "", amount: "", expected_date: "", probability: "high", recurring: false, kind: null, frequency: "monthly", end_date: "", original_currency: "", notes: "", tags: [], account_id: "" });
    setOpen(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setFieldErrors({});
    setForm({
      client_name: r.client_name,
      amount: String(r.original_amount || r.amount),
      expected_date: r.expected_date ? r.expected_date.split("T")[0] : "",
      probability: r.probability,
      recurring: !!r.recurring,
      kind: r.kind || (r.recurring ? "recurring" : null),
      frequency: r.frequency || "monthly",
      end_date: r.end_date ? r.end_date.split("T")[0] : "",
      original_currency: r.original_currency || "",
      notes: r.notes || "",
      tags: Array.isArray(r.tags) ? r.tags : [],
      account_id: r.account_id || "",
    });
    setOpen(true);
  };

  const save = async () => {
    // iter-169zze — track field-level errors so the UI can highlight
    // the offending inputs instead of toasting a vague message.
    const fe = {};
    if (!form.client_name?.trim()) {
      fe.client_name = isEs ? "El nombre del cliente es obligatorio." : "Client name is required.";
    }
    if (!form.amount || Number(form.amount) <= 0) {
      fe.amount = isEs ? "Indica un monto mayor a 0." : "Enter an amount greater than 0.";
    }
    if (!form.expected_date) {
      fe.expected_date = isEs ? "Elige la fecha esperada." : "Pick the expected date.";
    }
    if (Object.keys(fe).length) {
      setFieldErrors(fe);
      reportValidationError("recv-form", fe);
      toast.error(isEs ? "Faltan campos obligatorios." : "Missing required fields.");
      return;
    }
    setFieldErrors({});
    const customCurrency = form.original_currency && form.original_currency !== currency;
    const payload = {
      client_name: form.client_name,
      amount: Number(form.amount),
      expected_date: new Date(form.expected_date).toISOString(),
      probability: form.probability,
      status: editing?.status || "pending",
      recurring: !!form.recurring,
      kind: form.recurring ? (form.kind || "recurring") : null,
      frequency: form.recurring ? form.frequency : null,
      end_date: form.recurring && form.end_date ? new Date(form.end_date).toISOString() : null,
      // When the user picks a different currency in the form, the backend
      // converts it server-side via enrich_fx_fields and stamps the per-record
      // FX trail. The `amount` we send is the value in the user-stated currency
      // (e.g. 500 USD); backend writes the COP-converted amount + originals.
      ...(customCurrency ? { original_currency: form.original_currency } : {}),
      notes: form.notes,
      tags: form.tags,
      account_id: form.account_id || null,
    };
    try {
      if (editing) {
        const updated = await update.mutateAsync({ id: editing.id, payload });
        pushHighlight("receivable", updated?.id || editing.id);
      } else {
        const created = await create.mutateAsync(payload);
        pushHighlight("receivable", created?.id);
      }
      toast.success("Saved");
      setOpen(false);
    } catch (_e) { /* onError toast already fired by useReceivablesMutations */ }
  };

  const collect = async (r) => {
    // Same contract as /Payments mark-paid: no account_id ⇒ open picker
    // so the user explicitly chooses where the cash lands. Critical for
    // freelancers with multi-currency wallets — the previous "credit
    // primary by default" behaviour silently moved cash on the wrong
    // wallet when client paid in USD but primary was ARS.
    if (!r.account_id) {
      setPickerTarget(r);
      return;
    }
    await runCollect(r, null);
  };

  const runCollect = async (r, funding) => {
    // iter-169zze · bugfix — ``funding`` is the OBJECT the picker emits
    // (``{account_id}``, mirroring the Payments flow). Previously we
    // treated it as a raw string and ended up POSTing
    // ``{account_id: {account_id: "..."}}`` → Pydantic rejected it with
    // "Input should be a valid string". Spread the object instead so
    // ``collectMut`` receives ``{id, account_id}`` exactly like
    // ``markPaid`` does.
    try {
      pushHighlight("receivable", r.id);
      pushHighlight("movement_src_receivable", r.id);
      const arg = funding ? { id: r.id, ...funding } : r.id;
      await collectMut.mutateAsync(arg);
      toast.success(
        tx(lang,
          `✓ ${r.client_name} collected · now in Movements`,
          `✓ ${r.client_name} cobrado · ahora en Movimientos`,
          `✓ ${r.client_name} recebido · agora em Movimentos`,
        ),
        {
          duration: 5000,
          action: {
            label: tx(lang, "View", "Ver", "Ver"),
            onClick: () => navigate("/history"),
          },
        },
      );
    } catch (_e) { /* onError toast already fired by useReceivablesMutations */ }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      await removeMut.mutateAsync(deleteTarget.id);
      toast.success("Deleted");
      setDeleteTarget(null);
    } catch {
      toast.error("Delete failed");
    }
  };

  const probColor = (p) => ({
    high: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    medium: "bg-amber-50 text-amber-700 ring-amber-200",
    low: "bg-slate-100 text-slate-600 ring-slate-200",
  }[p]);

  // iter-195 — stat chips. Total pendiente / Atrasados / Próximos 7
  // días / Cantidad total. Values computed from currently-loaded items
  // so they stay in sync with the user's filters; ``count`` reflects
  // the items the user could potentially see ("4 pendientes").
  const statValues = (() => {
    let total = 0, overdue = 0, overdueAmount = 0, next7 = 0, next7Amount = 0;
    items.forEach((r) => {
      const amt = Number(r.amount) || 0;
      total += amt;
      const dueMs = new Date(r.expected_date || r.date).setHours(0, 0, 0, 0);
      if (dueMs < todayMs) { overdue += 1; overdueAmount += amt; }
      else if (dueMs <= next7Ms) { next7 += 1; next7Amount += amt; }
    });
    return { total, overdue, overdueAmount, next7, next7Amount };
  })();
  const fmtChipMoney = (n) => formatMoney(n, currency, { compact: true });
  const statChips = items.length > 0 ? [
    {
      id: "total", icon: Coins,
      label: isEs ? "Total pendiente" : "Total pending",
      value: fmtChipMoney(statValues.total),
      tone: "slate",
    },
    {
      id: "overdue", icon: AlertCircle,
      label: isEs ? "Atrasados" : "Overdue",
      value: `${statValues.overdue} · ${fmtChipMoney(statValues.overdueAmount)}`,
      tone: "rose",
      filterId: statValues.overdue > 0 ? "overdue" : null,
    },
    {
      id: "next7", icon: CalendarClock,
      label: isEs ? "Próximos 7 días" : "Next 7 days",
      value: `${statValues.next7} · ${fmtChipMoney(statValues.next7Amount)}`,
      tone: "amber",
      filterId: statValues.next7 > 0 ? "next7" : null,
    },
    {
      id: "count", icon: CalendarIcon,
      label: isEs ? "Cobros" : "Receivables",
      value: items.length,
      tone: "emerald",
    },
  ] : [];

  const renderRow = (r) => (
    <SwipeActions
      key={r.id}
      testid={`recv-swipe-${r.id}`}
      actions={[
        { label: tx(lang, "Edit", "Editar", "Editar"), icon: Pencil, color: "bg-sky-500", testid: `swipe-edit-recv-${r.id}`, onClick: () => openEdit(r) },
        { label: tx(lang, "Delete", "Eliminar", "Excluir"), icon: Trash2, color: "bg-rose-500", testid: `swipe-delete-recv-${r.id}`, onClick: () => setDeleteTarget(r) },
      ]}
    >
      <HighlightCard kind="receivable" id={r.id} data-testid={`receivable-${r.id}`} className="p-4 sm:p-5 rounded-2xl border-slate-200">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
              {r.client_name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate flex items-center gap-2">
                {r.client_name}
                {r.recurring && (() => {
                  const isSubscription = r.kind === "subscription";
                  const chipColors = isSubscription
                    ? "text-purple-700 bg-purple-50 ring-purple-200"
                    : "text-sky-700 bg-sky-50 ring-sky-200";
                  const chipLabel = isEs
                    ? (isSubscription ? "Suscripción" : "Periódico")
                    : (isSubscription ? "Subscription" : "Recurring");
                  return (
                    <span
                      className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ring-1 rounded-full px-1.5 py-0.5 ${chipColors}`}
                      data-testid={`recv-${isSubscription ? "subscription" : "recurring"}-chip-${r.id}`}
                    >
                      <RotateCw className="h-2.5 w-2.5" /> {chipLabel} · {localizeCadence(r.frequency || "monthly", lang)}
                    </span>
                  );
                })()}
              </div>
              <div className="text-xs text-slate-500">
                Due {new Date(r.expected_date).toLocaleDateString()}
                {r.recurring && (() => {
                  const nxt = nextRecurringDate(r.expected_date, r.frequency, r.end_date);
                  return nxt ? (
                    <span className="ml-2 text-sky-700" data-testid={`recv-next-${r.id}`}>
                      • {tx(lang, "next", "próxima", "próxima")} {formatNextDate(nxt, lang)}
                    </span>
                  ) : null;
                })()}
              </div>
              {r.notes && (
                <div data-testid={`recv-notes-${r.id}`} className="text-[11px] text-slate-500 mt-0.5 italic truncate max-w-xs" title={r.notes}>
                  “{r.notes}”
                </div>
              )}
              {Array.isArray(r.tags) && r.tags.length > 0 && (
                <div className="mt-1.5"><TagChips tags={r.tags} testid={`recv-tags-${r.id}`} /></div>
              )}
              {/* iter-220.1 — account chip (target account where the
                  collection will land). Receivables don't fund via
                  debt, so we only show account chip or "asignar
                  cuenta" CTA opening the edit dialog. */}
              <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                <AccountChip
                  item={r}
                  accounts={cashAccounts}
                  lang={lang}
                  testid={`recv-account-chip-${r.id}`}
                  missingFallback={
                    <button /* design-system: ALLOW legacy-todo */
                      type="button"
                      onClick={() => openEdit(r)}
                      data-testid={`recv-account-missing-${r.id}`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-900 hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:border-emerald-300 dark:hover:border-emerald-800 hover:shadow-sm transition-all"
                    >
                      <Wallet className="h-3 w-3" />
                      {isEs ? "Asignar cuenta" : "Assign account"}
                    </button>
                  }
                />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <span className={`text-[11px] px-2 py-1 rounded-full font-bold uppercase ring-1 ${probColor(r.probability)}`}>{r.probability}</span>
            <MoneyAmount
              amount={r.amount} currency={currency}
              original_currency={r.original_currency}
              original_amount={r.original_amount}
              fx_rate={r.fx_rate} fx_at={r.fx_at}
              size="md" testid={`recv-amount-${r.id}`}
            />
            {r.status === "pending" && (
              <Button data-testid={`collect-btn-${r.id}`} size="sm" variant="outline" className="gap-1" onClick={() => collect(r)}>
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t("mark_collected")}
              </Button>
            )}
            {r.recurring && r.status === "pending" && (
              <Button
                size="sm"
                variant="outline"
                className="gap-1 text-rose-700 border-rose-200 hover:bg-rose-50 hover:text-rose-800"
                data-testid={`cancel-sub-btn-${r.id}`}
                onClick={() => { setCancelTarget(r); setCancelReason(""); }}
              >
                <UserMinus className="h-4 w-4" /> {isEs ? "Cancelar" : "Cancel"}
              </Button>
            )}
            <Button size="icon" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-recv-${r.id}`} className="hidden sm:inline-flex"><Pencil className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" onClick={() => setDeleteTarget(r)} data-testid={`delete-recv-${r.id}`} className="hidden sm:inline-flex"><Trash2 className="h-4 w-4 text-red-600" /></Button>
          </div>
        </div>
        <LineItemsView items={r.line_items} amount={r.amount} currency={currency} reconciled={r.amount_reconciled} claimedAmount={r.claimed_amount} testid={`recv-items-${r.id}`} />
      </HighlightCard>
    </SwipeActions>
  );

  return (
    <Layout>
      <PageHeader
        eyebrow={tx(lang, "Inflows", "Entradas", "Entradas")}
        title={t("nav_receivables")}
        subtitle={tx(lang,
          `${items.length} pending — mark collected and they credit the target account.`,
          `${items.length} pendientes — marca cobrado y se acreditan en la cuenta destino.`,
          `${items.length} pendentes — marque como recebido e creditamos na conta destino.`,
        )}
        testid="receivables-header"
        actions={
          <Button data-testid="add-receivable-btn" onClick={openNew} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl gap-2">
            <Plus className="h-4 w-4" /> {t("new_receivable")}
          </Button>
        }
      />

      <GroupedListView
        testid="receivables-list"
        lang={lang}
        items={filteredItems}
        isLoading={isLoading}
        renderRow={renderRow}
        dateAccessor={(r) => r.expected_date || r.date}
        order="asc"
        restoredBanner={
          <RestoredFiltersBanner
            visible={filtersRestored && filtersActive}
            onClear={clearFilters}
            testid="receivables"
            lang={lang}
          />
        }
        extraFilters={
          <Select
            value={accountFilter || "_all"}
            onValueChange={(v) => setAccountFilter(v === "_all" ? "" : v)}
          >
            <SelectTrigger
              className="h-9 w-[170px] text-xs"
              data-testid="recv-filter-account"
            >
              <SelectValue placeholder={tx(lang, "All accounts", "Todas las cuentas", "Todas as contas")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_all">{tx(lang, "All accounts", "Todas las cuentas", "Todas as contas")}</SelectItem>
              {(cashAccounts || []).map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={tx(lang, "Search client…", "Buscar cliente…", "Buscar cliente…")}
        dateFrom={dateFrom} dateTo={dateTo}
        onDateFromChange={setDateFrom} onDateToChange={setDateTo}
        tagSuggestions={tagSuggestions}
        tagFilter={tagFilter}
        onTagFilterChange={setTagFilter}
        statChips={statChips}
        activeChip={quickFilter}
        onChipClick={setQuickFilter}
        sentinelRef={sentinelRef}
        hasMore={hasMore}
        isFetchingMore={isFetchingNextPage}
        // eslint-disable-next-line react/no-unstable-nested-components
        groupSummary={(groupItems) => {
          const sum = groupItems.reduce((a, r) => a + (Number(r.amount) || 0), 0);
          return (
            <span className="text-sm font-bold tabular-nums text-emerald-700">
              +{formatMoney(sum, currency)}
            </span>
          );
        }}
        emptyState={
          <Card className="p-8 text-center text-slate-500 rounded-2xl border-dashed">
            {isEs ? "Aún no tienes cobros pendientes." : "No pending receivables yet."}
          </Card>
        }
      />


      <BottomSheet
        open={open}
        onOpenChange={setOpen}
        testid="receivable-dialog"
        size="lg"
        title={editing ? t("edit") : t("new_receivable")}
        description={editing ? t("edit") : t("new_receivable")}
        footer={
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="h-11">{t("cancel")}</Button>
            <Button data-testid="recv-form-save" onClick={save} className="bg-emerald-600 hover:bg-emerald-700 h-11 px-5 font-semibold">{t("save")}</Button>
          </div>
        }
      >
        <div className="space-y-3">
            <div>
              <Label htmlFor="recv-form-client" className="flex items-center gap-1">
                {t("client_name")}
                <span className="text-rose-500" aria-hidden="true">*</span>
              </Label>
              <Input
                id="recv-form-client"
                data-testid="recv-form-client"
                autoComplete="off"
                value={form.client_name}
                onChange={(e) => { setForm({ ...form, client_name: e.target.value }); if (fieldErrors.client_name) setFieldErrors({ ...fieldErrors, client_name: undefined }); }}
                aria-invalid={fieldErrors.client_name ? "true" : undefined}
                aria-describedby={fieldErrors.client_name ? "recv-client-err" : undefined}
                className={fieldErrors.client_name ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}
              />
              {fieldErrors.client_name && (
                <p id="recv-client-err" role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="recv-form-client-error">
                  {fieldErrors.client_name}
                </p>
              )}
            </div>
            <div className="grid grid-cols-[1fr_120px_140px] sm:grid-cols-[1fr_120px_140px] gap-3">
              <div>
                <Label htmlFor="recv-form-amount" className="flex items-center gap-1">
                  {t("amount")}
                  <span className="text-rose-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="recv-form-amount"
                  data-testid="recv-form-amount"
                  type="number"
                  value={form.amount}
                  onChange={(e) => { setForm({ ...form, amount: e.target.value }); if (fieldErrors.amount) setFieldErrors({ ...fieldErrors, amount: undefined }); }}
                  aria-invalid={fieldErrors.amount ? "true" : undefined}
                  aria-describedby={fieldErrors.amount ? "recv-amount-err" : undefined}
                  className={fieldErrors.amount ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}
                />
                {fieldErrors.amount && (
                  <p id="recv-amount-err" role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="recv-form-amount-error">
                    {fieldErrors.amount}
                  </p>
                )}
              </div>
              <div>
                <Label>{t("currency") || "Currency"}</Label>
                <Select value={form.original_currency || currency} onValueChange={(v) => setForm({ ...form, original_currency: v === currency ? "" : v })}>
                  <SelectTrigger data-testid="recv-form-currency"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {(fxList.length ? fxList : [currency]).map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="recv-form-date" className="flex items-center gap-1">
                  {t("expected_date")}
                  <span className="text-rose-500" aria-hidden="true">*</span>
                </Label>
                <Input
                  id="recv-form-date"
                  data-testid="recv-form-date"
                  type="date"
                  value={form.expected_date}
                  onChange={(e) => { setForm({ ...form, expected_date: e.target.value }); if (fieldErrors.expected_date) setFieldErrors({ ...fieldErrors, expected_date: undefined }); }}
                  aria-invalid={fieldErrors.expected_date ? "true" : undefined}
                  aria-describedby={fieldErrors.expected_date ? "recv-date-err" : undefined}
                  className={fieldErrors.expected_date ? "border-rose-300 focus-visible:ring-rose-300/40" : ""}
                />
                {fieldErrors.expected_date && (
                  <p id="recv-date-err" role="alert" className="text-[11px] text-rose-600 font-medium mt-1 form-err-msg" data-testid="recv-form-date-error">
                    {fieldErrors.expected_date}
                  </p>
                )}
              </div>
            </div>
            <div>
              <Label>{t("probability")}</Label>
              <Select value={form.probability} onValueChange={(v) => setForm({ ...form, probability: v })}>
                <SelectTrigger data-testid="recv-form-prob"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">{t("high")}</SelectItem>
                  <SelectItem value="medium">{t("medium")}</SelectItem>
                  <SelectItem value="low">{t("low")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border-t border-slate-200 pt-3 space-y-2">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {isEs ? "Tipo de cobro" : "Charge type"}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className={`flex items-start gap-2 cursor-pointer rounded-xl border p-3 transition-colors ${
                  form.recurring && form.kind === "recurring"
                    ? "border-sky-300 bg-sky-50/60"
                    : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input /* design-system: ALLOW legacy-todo */
                    data-testid="recv-form-recurring"
                    type="checkbox"
                    checked={form.recurring && form.kind === "recurring"}
                    onChange={(e) => setForm({
                      ...form,
                      recurring: e.target.checked,
                      kind: e.target.checked ? "recurring" : null,
                    })}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-900 block">
                      {isEs ? "Es un cobro periódico" : "It's a periodic income"}
                    </span>
                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                      {isEs
                        ? "Trabajo continuo: sueldo o retainer. Suele no tener fecha de fin."
                        : "Continuous work: salary or retainer. Usually no end date."}
                    </p>
                  </div>
                </label>
                <label className={`flex items-start gap-2 cursor-pointer rounded-xl border p-3 transition-colors ${
                  form.recurring && form.kind === "subscription"
                    ? "border-purple-300 bg-purple-50/60"
                    : "border-slate-200 hover:border-slate-300"
                }`}>
                  <input /* design-system: ALLOW legacy-todo */
                    data-testid="recv-form-subscription"
                    type="checkbox"
                    checked={form.recurring && form.kind === "subscription"}
                    onChange={(e) => setForm({
                      ...form,
                      recurring: e.target.checked,
                      kind: e.target.checked ? "subscription" : null,
                    })}
                    className="mt-0.5 h-4 w-4 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-slate-900 block">
                      {isEs ? "Es una suscripción" : "It's a subscription"}
                    </span>
                    <p className="text-[11px] text-slate-500 leading-snug mt-0.5">
                      {isEs
                        ? "Cliente con contrato/membresía. Cancelarlo genera alerta de churn."
                        : "Client with contract/membership. Cancelling triggers a churn alert."}
                    </p>
                  </div>
                </label>
              </div>
              {form.recurring && (
                <div className="mt-3 grid grid-cols-2 gap-3" data-testid="recv-form-recurring-params">
                  <div>
                    <Label>{t("frequency")}</Label>
                    <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                      <SelectTrigger data-testid="recv-form-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">{t("weekly")}</SelectItem>
                        <SelectItem value="biweekly">{t("biweekly")}</SelectItem>
                        <SelectItem value="monthly">{t("monthly")}</SelectItem>
                        <SelectItem value="quarterly">{t("quarterly")}</SelectItem>
                        <SelectItem value="yearly">{t("yearly")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t("end_date")}</Label>
                    <Input data-testid="recv-form-end-date" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
                  </div>
                </div>
              )}
              {form.recurring && form.expected_date && (() => {
                const nxt = nextRecurringDate(form.expected_date, form.frequency, form.end_date);
                return nxt ? (
                  <p className="mt-2 text-xs text-sky-700" data-testid="recv-form-next-preview">
                    {tx(lang,
                      `Next occurrence: ${formatNextDate(nxt, lang)}`,
                      `Próxima ocurrencia: ${formatNextDate(nxt, lang)}`,
                      `Próxima ocorrência: ${formatNextDate(nxt, lang)}`,
                    )}
                  </p>
                ) : null;
              })()}
              <div className="mt-3">
                <Label>{tx(lang, "Credit to account", "Acreditar a cuenta", "Creditar na conta")}</Label>
                <Select
                  value={form.account_id || "__primary__"}
                  onValueChange={(v) => setForm({ ...form, account_id: v === "__primary__" ? "" : v })}
                >
                  <SelectTrigger data-testid="recv-form-account"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__primary__">
                      {tx(lang, "Primary account (auto)", "Cuenta principal (auto)", "Conta principal (auto)")}
                    </SelectItem>
                    {cashAccounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.name}{a.institution ? ` · ${a.institution}` : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-slate-500 mt-1">
                  {tx(lang,
                    "When you mark the receivable as collected, we'll credit this account.",
                    "Al marcar el cobro como recibido, sumaremos el monto a esta cuenta.",
                    "Quando você marcar como recebido, creditamos o valor nessa conta.",
                  )}
                </p>
              </div>
              <div className="mt-3">
                <Label>{tx(lang, "Notes", "Notas", "Notas")}</Label>
                <textarea /* design-system: ALLOW legacy-todo */
                  data-testid="recv-form-notes"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  rows={2}
                  placeholder={tx(lang,
                    "Free-form detail — invoice number, payment terms…",
                    "Detalle libre — número de factura, condiciones de pago…",
                    "Detalhe livre — número de fatura, condições de pagamento…",
                  )}
                  className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
                />
              </div>
              <div className="mt-3">
                <Label>{tx(lang, "Tags", "Etiquetas", "Tags")}</Label>
                <TagsInput
                  value={form.tags}
                  onChange={(tags) => setForm({ ...form, tags })}
                  suggestions={tagSuggestions}
                  placeholder={tx(lang, "billable, priority…", "facturable, prioridad…", "faturável, prioridade…")}
                  testid="recv-form-tags"
                />
                <p className="text-[10px] text-slate-400 mt-1">
                  {tx(lang,
                    "You can also write #tag inline in the notes.",
                    "También puedes usar #etiqueta dentro de las notas.",
                    "Você também pode usar #tag dentro das notas.",
                  )}
                </p>
              </div>
            </div>
          </div>
      </BottomSheet>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="recv-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("confirm_delete_recv", { name: deleteTarget?.client_name || "" })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="recv-delete-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="recv-delete-confirm"
              onClick={remove}
              className="bg-red-600 hover:bg-red-700"
            >
              {t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* iter-211 — Cancel-subscription dialog. Reason is optional but
          stored on `churn_events.reason` and surfaced on the dashboard. */}
      <AlertDialog open={!!cancelTarget} onOpenChange={(o) => { if (!o) { setCancelTarget(null); setCancelReason(""); } }}>
        <AlertDialogContent data-testid="recv-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <UserMinus className="h-5 w-5 text-rose-600" />
              {isEs ? "Cancelar suscripción" : "Cancel subscription"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isEs
                ? <>Vas a cancelar la suscripción de <span className="font-semibold text-slate-900">{cancelTarget?.client_name}</span>. Dejará de sumar a tu MRR y aparecerá en tus alertas de churn.</>
                : <>You&apos;re cancelling the subscription with <span className="font-semibold text-slate-900">{cancelTarget?.client_name}</span>. It will stop contributing to MRR and show up in your churn alerts.</>
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="mt-2 space-y-1">
            <Label className="text-xs">
              {isEs ? "Motivo (opcional)" : "Reason (optional)"}
            </Label>
            <textarea
              data-testid="recv-cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value.slice(0, 280))}
              rows={2}
              placeholder={isEs
                ? "Cliente cerró el proyecto / cambió de proveedor / pausó por presupuesto…"
                : "Client ended the project / switched providers / paused on budget…"}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 resize-none"
            />
            <p className="text-[10px] text-slate-400 text-right tabular-nums">{cancelReason.length}/280</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={cancelSubmitting} data-testid="recv-cancel-dismiss">
              {isEs ? "Volver" : "Back"}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="recv-cancel-confirm"
              disabled={cancelSubmitting}
              onClick={async (e) => {
                e.preventDefault();
                if (!cancelTarget?.id) return;
                setCancelSubmitting(true);
                try {
                  const res = await receivablesService.cancelSubscription(cancelTarget.id, cancelReason);
                  toast.success(isEs
                    ? `Suscripción cancelada. Impacto 90d: −${formatMoney(res.impact_90d, res.currency)}`
                    : `Subscription cancelled. 90d impact: −${formatMoney(res.impact_90d, res.currency)}`);
                  queryClient.invalidateQueries({ queryKey: ["receivables"] });
                  queryClient.invalidateQueries({ queryKey: ["dashboard"] });
                  queryClient.invalidateQueries({ queryKey: ["mrr"] });
                  queryClient.invalidateQueries({ queryKey: ["churn"] });
                  setCancelTarget(null);
                  setCancelReason("");
                } catch (err) {
                  const detail = err?.response?.data?.detail;
                  toast.error(isEs
                    ? `No se pudo cancelar${detail ? `: ${detail}` : ""}.`
                    : `Couldn't cancel${detail ? `: ${detail}` : ""}.`);
                } finally {
                  setCancelSubmitting(false);
                }
              }}
              className="bg-rose-600 hover:bg-rose-700 gap-1.5"
            >
              {cancelSubmitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserMinus className="h-3.5 w-3.5" />}
              {isEs ? "Cancelar suscripción" : "Cancel subscription"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Target-account picker — same contract as Payments: fires when
          collecting a receivable that has no account_id linked. */}
      <AccountPickerDialog
        target={pickerTarget}
        mode="collect"
        cashAccounts={cashAccounts}
        lang={lang}
        onClose={() => setPickerTarget(null)}
        onSubmit={async (funding) => {
          const r = pickerTarget;
          setPickerTarget(null);
          await runCollect(r, funding);
        }}
      />
    </Layout>
  );
}
